/**
 * Exact-snapshot Zenoh client — SPA architectural convergence plan §6.1.
 *
 * Reads an exact category snapshot from the internal Zenoh REST/SSE
 * plugin (`zenoh-webserver`) behind Envoy's `/live/v1/` route — the
 * same wire shape `fetch-sse.ts` consumes over SSE, fetched once as
 * a plain GET for initial-load hydration.
 *
 * The plugin is the stock `eclipse/zenoh` REST peer: a GET against a
 * keyexpr pattern returns `Array<{key, value, encoding, timestamp}>`
 * where `value` is the JSON body each v1 publisher PUT — an envelope
 * + payload pair (`{envelope: {...}, payload: {...}}`), exactly the
 * shape `V1LiveEvent`'s `snapshot`/`event` variants carry. This
 * module decodes that shape and never talks to `nexsoss-api-server`
 * (plan §Locked architecture §5: "The API must not aggregate live
 * current state for browser reads.").
 */
import { getCurrentToken } from '../auth/token-store';
import { getRuntimeConfig } from '../config/runtime-config';
import { isValidEnvelope } from './v1-envelope';
import type { DoorState, FleetRegistry, LiftState, RobotState, SiteId, TaskSummary, V1Envelope } from './v1';

interface ZenohSample {
  key?: string;
  value?: unknown;
  encoding?: string;
  timestamp?: number | number[];
}

function parseSampleArray(res: unknown): ZenohSample[] {
  if (Array.isArray(res)) return res as ZenohSample[];
  const r = res as { results?: ZenohSample[] } | null;
  if (r && Array.isArray(r.results)) return r.results;
  return [];
}

/**
 * The Zenoh REST plugin stores PUT bodies as a JSON-serialised
 * string and returns them verbatim as the sample's `value` field;
 * tolerate an already-parsed object too (defensive, mirrors the
 * SSE parser's `JSON.parse` in `fetch-sse.ts`).
 */
function parseSampleValue(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return value;
}

interface SnapshotBody {
  envelope?: V1Envelope;
  payload?: unknown;
}

/**
 * Fetch every sample under a category's live keyexpr and decode the
 * envelope + payload each v1 publisher writes. Samples with a
 * missing or unrecognised `schema_version` are dropped rather than
 * rendered (plan §Live consistency protocol).
 */
interface CategorySample {
  payload: unknown;
  /** Envelope `projector_received_at_ms` — when the platform last
   *  published this entity. Carried out of here because it is the
   *  only field that can tell a quiet task from a busy one; the
   *  payload's own timestamps say when work STARTED, not when it was
   *  last heard from. */
  receivedAtMs: number;
}

async function fetchCategorySamples(site: SiteId, category: string): Promise<CategorySample[]> {
  const cfg = getRuntimeConfig();
  const token = getCurrentToken();
  const url = `${cfg.apiBaseUrl}/live/v1/sites/${encodeURIComponent(site)}/${category}/**`;
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${category} snapshot: HTTP ${res.status}`);
  }
  const samples = parseSampleArray(await res.json());
  const out: CategorySample[] = [];
  for (const s of samples) {
    const body = parseSampleValue(s.value) as SnapshotBody | undefined;
    if (!body || !isValidEnvelope(body.envelope) || body.payload === undefined) continue;
    out.push({ payload: body.payload, receivedAtMs: body.envelope.projector_received_at_ms });
  }
  return out;
}

function payloadsOf(samples: CategorySample[]): unknown[] {
  return samples.map((s) => s.payload);
}

export async function fetchFleetsSnapshot(site: SiteId): Promise<FleetRegistry[]> {
  return payloadsOf(await fetchCategorySamples(site, 'fleets')) as FleetRegistry[];
}

export async function fetchRobotsSnapshot(site: SiteId): Promise<RobotState[]> {
  return payloadsOf(await fetchCategorySamples(site, 'robots')) as RobotState[];
}

export async function fetchDoorsSnapshot(site: SiteId): Promise<DoorState[]> {
  return payloadsOf(await fetchCategorySamples(site, 'doors')) as DoorState[];
}

export async function fetchLiftsSnapshot(site: SiteId): Promise<LiftState[]> {
  return payloadsOf(await fetchCategorySamples(site, 'lifts')) as LiftState[];
}

/** A projected task plus the envelope timestamp of its last publication. */
export interface TaskSnapshotEntry {
  task: TaskSummary;
  receivedAtMs: number;
}

/**
 * Tasks are the one category that keeps its envelope timestamp: the
 * quiet-task badge needs to know when the platform last said anything
 * about a task, and an orphaned task's payload looks perfectly healthy
 * (`patrol.dispatch-48` still read `state: "active"` 45 h after the
 * adapter that owned it was replaced).
 */
export async function fetchTasksSnapshot(site: SiteId): Promise<TaskSnapshotEntry[]> {
  const samples = await fetchCategorySamples(site, 'tasks');
  return samples.map((s) => ({ task: s.payload as TaskSummary, receivedAtMs: s.receivedAtMs }));
}
